import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import {
  apagarAvisoDoFimDoVerComo,
  esquecerSessaoVerComo,
  lerAvisoDoFimDoVerComo,
  sairDoVerComoSemAvisarOBanco,
  sessaoVerComo,
  type SessaoVerComo,
} from "../lib/verComo";
import { IconeFechar, IconeOlho } from "./icones";

/**
 * A barra do «ver como» (000335): fixa no pé de TODA tela enquanto o master vê o
 * hub como outra pessoa, com o «Voltar ao normal» sempre à mão — o pedido de
 * Victor. Mora em `App`, fora das rotas, para nenhuma tela precisar lembrar dela.
 *
 * No PÉ, e não no topo: o `Topo` é sticky em `top-0`, e uma barra acima dele
 * empurraria cada tela de altura exata (a IA de curso) para fora do viewport. No
 * pé, quem abre espaço é o `body` (`html[data-ver-como] body` no `index.css`),
 * com a altura MEDIDA desta barra em `--cf-ver-como` — no celular o texto quebra
 * em mais linhas, e cravar um número enterraria o fim da página atrás dela. A
 * tela de altura exata desconta a mesma variável (`Ferramenta.tsx`).
 *
 * Cor de MARCA e não semântica: é território navy nos dois temas (a regra da
 * casa), e sobre ele `navy-ink`/`navy-mute`.
 *
 * Fora do «ver como», ela só mostra — uma vez — o aviso de que ele terminou sem
 * o master pedir (venceu, foi encerrado em outra aba, o banco não reconheceu).
 */
export function BarraVerComo() {
  const { sessao } = useAuth();
  const ativa = sessaoVerComo();
  if (ativa) return sessao ? <Barra ativa={ativa} /> : null;
  return <AvisoDoFim />;
}

function Barra({ ativa }: { ativa: SessaoVerComo }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [voltando, setVoltando] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const raiz = document.documentElement;
    const medir = () => raiz.style.setProperty("--cf-ver-como", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => {
      obs.disconnect();
      raiz.style.removeProperty("--cf-ver-como");
    };
  }, []);

  // O banco reconhece ESTA sessão? Sem o gancho de pré-requisição (desligado à
  // mão, por exemplo), o cabeçalho seria ignorado e a tela de baixo seria a do
  // próprio master com esta barra dizendo outra pessoa — então ela sai sozinha.
  // A recusa do gancho (sessão vencida, encerrada) não chega aqui: o `fetch` do
  // «ver como» já sai ao ler a resposta.
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data, error } = await supabase.rpc("ver_como_estado");
      if (!vivo || error) return;
      const e = data as { ativo?: boolean; alvo?: { id?: string } } | null;
      if (e?.ativo !== true || e.alvo?.id !== ativa.alvo.id) {
        sairDoVerComoSemAvisarOBanco("A visualização terminou: o banco não reconheceu a sessão desta aba.");
      }
    })();
    return () => {
      vivo = false;
    };
  }, [ativa.alvo.id]);

  // O prazo vence com a aba aberta: sai na hora, em vez de esperar o próximo clique.
  useEffect(() => {
    const falta = Date.parse(ativa.expira_em) - Date.now();
    const t = window.setTimeout(
      () => sairDoVerComoSemAvisarOBanco("A visualização como outra pessoa terminou: ela vale uma hora."),
      Math.max(0, Math.min(falta, 2_147_000_000)),
    );
    return () => window.clearTimeout(t);
  }, [ativa.expira_em]);

  async function voltar() {
    setVoltando(true);
    // Esquecer ANTES de encerrar: com a sessão nesta aba, o pedido levaria o
    // cabeçalho, e a transação somente leitura recusaria o encerramento.
    esquecerSessaoVerComo();
    try {
      await supabase.rpc("ver_como_encerrar", { p_sessao: ativa.token });
    } catch {
      /* a sessão vence sozinha em uma hora; voltar não depende disto */
    }
    window.location.assign(ativa.voltar_para);
  }

  const quem = ativa.alvo.nome?.trim() || ativa.alvo.email;
  const ate = new Date(ativa.expira_em).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Ver como outra pessoa"
      data-ver-como-barra=""
      className="fixed inset-x-0 bottom-0 z-[60] border-t-2 border-laranja bg-azul text-navy-ink shadow-[0_-10px_28px_-14px_rgba(5,13,28,0.7)]"
    >
      {/* No celular a barra tem de ser CURTA: a primeira versão, com o e-mail e
          a frase inteira, media 183px de 844 (medido em 02/10, a 390px). Lá
          ficam o nome numa linha e "só leitura"; o resto aparece a partir de sm. */}
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2 sm:gap-4 sm:px-6 sm:py-2.5">
        <IconeOlho className="hidden size-5 shrink-0 text-laranja sm:block" />
        <p className="min-w-0 flex-1 text-sm leading-snug">
          <span className="block truncate font-semibold text-white sm:inline" title={quem}>
            <span className="sm:hidden">Vendo como </span>
            <span className="hidden sm:inline">Vendo o hub como </span>
            <span data-ver-como-alvo="">{quem}</span>
          </span>
          {ativa.alvo.nome?.trim() && (
            <span className="hidden text-navy-mute [overflow-wrap:anywhere] sm:inline"> · {ativa.alvo.email}</span>
          )}
          <span className="block text-xs text-navy-mute" data-ver-como-apoio="">
            <span className="sm:hidden">Só leitura · vale até {ate}</span>
            <span className="hidden sm:inline">
              Só leitura: nada é gravado, e o que abre outro sistema fica desligado. Vale até {ate}.
            </span>
          </span>
        </p>
        <button
          type="button"
          onClick={voltar}
          disabled={voltando}
          className="pill-cta shrink-0 px-3.5 py-2 text-sm sm:px-4"
          data-ver-como-voltar=""
        >
          {voltando ? "Voltando…" : "Voltar ao normal"}
        </button>
      </div>
    </div>
  );
}

function AvisoDoFim() {
  // Ler sem apagar no render, e apagar no efeito: no modo estrito o React chama
  // o inicializador duas vezes, e um inicializador que apaga deixaria a segunda
  // chamada sem o texto.
  const [texto, setTexto] = useState<string | null>(() => lerAvisoDoFimDoVerComo());
  useEffect(() => {
    if (texto) apagarAvisoDoFimDoVerComo();
  }, [texto]);
  if (!texto) return null;
  return (
    <div
      role="status"
      data-ver-como-fim=""
      className="fixed inset-x-4 bottom-4 z-[60] mx-auto flex max-w-xl items-start gap-3 rounded-cf border border-borda border-l-4 border-l-laranja bg-superficie px-4 py-3 text-sm text-tinta shadow-[0_18px_40px_-18px_rgba(5,13,28,0.55)]"
    >
      <p className="min-w-0 flex-1 leading-relaxed">{texto}</p>
      <button
        type="button"
        onClick={() => setTexto(null)}
        className="-m-1 shrink-0 rounded-full p-1 text-cinza hover:text-tinta"
        aria-label="Fechar o aviso"
      >
        <IconeFechar className="size-4" />
      </button>
    </div>
  );
}
